// AuthContext.jsx
import { createContext, useContext, useEffect, useState } from "react";
import axios from "../api/axiosConfig";
import i18n  from "../i18n";

const AuthContext = createContext();

// ── Language resolution ───────────────────────────────────────────────────────
// Flow (simplified — only ONE extra call now, not two):
//   1. GET /api/app_user/me  → AppUserDto { id, languageId, role, … }
//   2. GET /languages/view   → LanguageDto[] { id, code }
//   3. find code where id === languageId → i18n.changeLanguage(code)
//
// Works for ALL roles (CLIENT, MANAGER, GUIDE) because languageId now
// lives on AppUser, not on Customer.
//
// Falls back to 'en' when:
//   - user is not logged in
//   - languageId is null (no language set yet)
//   - language code is not one of ['en','ru','es'] (unsupported locale)
//   - any network error

const SUPPORTED = new Set(['en', 'ru', 'es']);

async function resolveAndSetLanguage(languageId) {
    if (!languageId) {
        if (i18n.language !== 'en') await i18n.changeLanguage('en');
        return 'en';
    }
    try {
        const langsRes  = await axios.get('/languages/view');
        const languages = Array.isArray(langsRes.data) ? langsRes.data : (langsRes.data?.content || []);
        const match     = languages.find(l => l.id === languageId);

        if (match?.code) {
            // If the DB code has a real translation file → use it.
            // Otherwise fall back to EN (i18next also does this via fallbackLng,
            // but being explicit avoids a flash of untranslated keys).
            const code = SUPPORTED.has(match.code) ? match.code : 'en';
            if (i18n.language !== code) await i18n.changeLanguage(code);
            return code;
        }
    } catch { /* silent — fallback below */ }

    if (i18n.language !== 'en') await i18n.changeLanguage('en');
    return 'en';
}

// ── Provider ──────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
    const [user,    setUser]    = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = async () => {
        try {
            const res = await axios.get("/api/app_user/me");
            setUser(res.data);
            // languageId is now a top-level field on AppUserDto — no second user call
            resolveAndSetLanguage(res.data.languageId);
        } catch {
            setUser(null);
            if (i18n.language !== 'en') i18n.changeLanguage('en');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { checkAuth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const logout = async () => {
        try { await axios.post("/api/app_user/logout"); } catch { /* silent */ }
        setUser(null);
        i18n.changeLanguage('en');
    };

    return (
        <AuthContext.Provider value={{ user, setUser, logout, loading, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);